import { sonarSdkConfig, type EnvironmentId } from '@/config/environments';

type SonarSession = { token: string; agentId?: string };

declare global {
  interface Window {
    MelioSonar?: {
      createSession: (publishableKey: string, options?: { businessEntityId?: string }) => Promise<SonarSession>;
    };
  }
}

const INIT_TIMEOUT_MS = 8000;

const scriptPromises = new Map<string, Promise<void>>();
const sessionPromises = new Map<string, Promise<SonarSession | null>>();

function loadScript(src: string): Promise<void> {
  const existing = scriptPromises.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    if (window.MelioSonar) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.addEventListener('load', () => resolve());
    el.addEventListener('error', () => reject(new Error(`failed to load ${src}`)));
    document.head.appendChild(el);
  });

  scriptPromises.set(src, promise);
  return promise;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('sonar sdk timed out')), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export function ensureSonarSession(env: EnvironmentId, publishableKey: string, businessEntityId?: string): Promise<SonarSession | null> {
  const cacheKey = `${env}:${publishableKey}:${businessEntityId ?? ''}`;
  const cached = sessionPromises.get(cacheKey);
  if (cached) return cached;

  const cfg = sonarSdkConfig(env);
  if (!cfg) return Promise.resolve(null);

  const promise = withTimeout(
    loadScript(cfg.scriptUrl).then(() => {
      if (!window.MelioSonar) throw new Error('MelioSonar global not available');
      return window.MelioSonar.createSession(publishableKey, { businessEntityId });
    }),
    INIT_TIMEOUT_MS
  )
    .then((session) => session ?? null)
    .catch(() => null);

  sessionPromises.set(cacheKey, promise);
  return promise;
}

export async function getSonarSessionToken(
  env: EnvironmentId,
  publishableKey: string,
  businessEntityId?: string
): Promise<string | undefined> {
  const session = await ensureSonarSession(env, publishableKey, businessEntityId);
  return session?.token;
}
